import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { removeAnimationObject } from '../../redux/Slice/animationSlice';
import AnimationLeftPanel from './AnimationLeftPanel';
import AnimationTimelinePanel from './AnimationTimelinePanel';
import AnimationRightPanel from './AnimationRightPanel';

// Custom hooks
import { useAnimationState } from './hooks/useAnimationState';
import { useTimelineHandlers } from './hooks/useTimelineHandlers'; // Uses timelineContainerRef
import { usePlaybackHandlers } from './hooks/usePlaybackHandlers';
import { useAnimationSettings } from './hooks/useAnimationSettings';

// Utilities
import {
  calculateTimelineBounds,
  timeToPercent,
  calculateTimelinePosition,
  roundToPrecision,
  parseFloatWithPrecision,
  findNextAvailableRow,
} from './utils/timelineUtils';

// Constants
import {
  ANIMATION_EFFECTS,
  TIMELINE_CONFIG,
} from './constants/animationConstants';

const AnimationComponentNew = ({
  onMinimizeChange,
  completeImageObject,
  window_type,
  initialMinimized = true,
  forceShow = false,
  // Increment this value to force-open the panel (useful for external triggers)
  forceShowSignal = 0,
}) => {
  const dispatch = useDispatch();

  const safeOnMinimizeChange = useMemo(() => {
    return typeof onMinimizeChange === 'function' ? onMinimizeChange : () => {};
  }, [onMinimizeChange]);

  const state = useAnimationState(safeOnMinimizeChange, initialMinimized);

  // Track if we've already applied forceShowSignal (persisted across unmount/remount)
  // Initialize from window object if available
  const lastForceShowSignalRef = useRef(
    window.__lastAnimationForceShowSignal || 0
  );

  useEffect(() => {
    // Only force-open if this is a NEW signal (value changed from what we last applied)
    if (
      (forceShow || forceShowSignal) &&
      forceShowSignal !== lastForceShowSignalRef.current
    ) {
      lastForceShowSignalRef.current = forceShowSignal;
      window.__lastAnimationForceShowSignal = forceShowSignal;
      state.setIsMinimized(false);
    }
  }, [forceShow, forceShowSignal, state.setIsMinimized]);

  // Add ref for timeline container (required by new useTimelineHandlers)
  const timelineContainerRef = useRef(null);

  // Pass timelineContainerRef into the handler hook
  const timelineHandlers = useTimelineHandlers({
    ...state,
    timelineContainerRef, // This is the only required addition
  });

  const playbackHandlers = usePlaybackHandlers(state, state);
  const animationSettingsHandlers = useAnimationSettings(state, state);

  // Mouse handlers for resizing (unchanged)
  const handleMouseDown = (e) => {
    state.setIsDragging(true);
    state.setDragStartY(e.clientY);
    state.setDragStartHeight(
      Math.min(TIMELINE_CONFIG.MAX_HEIGHT, state.animationHeight)
    );
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
  };

  const handleMouseMove = (e) => {
    if (!state.isDragging) return;
    const deltaY = state.dragStartY - e.clientY;
    let newHeight = state.dragStartHeight + deltaY;

    if (newHeight <= TIMELINE_CONFIG.MIN_HEIGHT) {
      state.setIsMinimized(true);
      return;
    }

    if (newHeight > TIMELINE_CONFIG.MIN_HEIGHT && state.isMinimized) {
      state.setIsMinimized(false);
    }

    newHeight = Math.max(
      TIMELINE_CONFIG.MIN_HEIGHT,
      Math.min(TIMELINE_CONFIG.MAX_HEIGHT, newHeight)
    );
    state.setAnimationHeight(newHeight);
  };

  const handleMouseUp = () => {
    state.setIsDragging(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  useEffect(() => {
    if (state.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [state.isDragging, state.dragStartY, state.dragStartHeight]);

  // Timeline bounds (unchanged)
  const timelineBounds = useMemo(() => {
    return calculateTimelineBounds(state.timelineEffects, state.effectEndTime);
  }, [state.timelineEffects, state.effectEndTime]);

  // Timeline bounds: derive max duration from effects (single effect, no feedback loop)
  useEffect(() => {
    if (!state.timelineEffects.length) return;

    const maxTime = Math.max(
      ...state.timelineEffects.map((effect) => effect.endTime || 0),
      7
    );
    const newMaxDuration = maxTime + 2;

    state.setMaxTimelineDuration((prev) =>
      prev < newMaxDuration ? newMaxDuration : prev
    );
    state.setEffectEndTime((prev) =>
      prev < newMaxDuration ? newMaxDuration : prev
    );
  }, [state.timelineEffects]);

  useEffect(() => {
    const minDuration = 1;

    if (state.effectStartTime < 0) {
      state.setEffectStartTime(0);
      return;
    }

    if (state.effectEndTime - state.effectStartTime < minDuration) {
      state.setEffectEndTime(state.effectStartTime + minDuration);
    }
  }, [state.effectStartTime, state.effectEndTime]);

  const lastGlobalObjId = useRef(null);

  const {
    timelineEffects,
    selectedObject,
    selectedEffects,
    setSelectedObject,
    setExpandedItems,
    setSelectedEffects,
  } = state;

  // Sync global selection (AppLeftSection) -> Animation Component
  useEffect(() => {
    const globalSelectedObj = completeImageObject?.selectedObject;
    if (!globalSelectedObj) return;

    const globalObjId =
      globalSelectedObj.name ||
      globalSelectedObj.uuid ||
      globalSelectedObj._id ||
      globalSelectedObj.url;

    if (!globalObjId || lastGlobalObjId.current === globalObjId) return;

    lastGlobalObjId.current = globalObjId;

    if (selectedObject !== globalObjId) {
      setSelectedObject(globalObjId);
      setExpandedItems((prev) => ({ ...prev, [globalObjId]: true }));

      const objEffects = timelineEffects.filter(
        (e) => e.objectId === globalObjId
      );

      if (objEffects.length > 0) {
        const hasExistingSelectionForObj = selectedEffects.some((id) =>
          objEffects.some((effect) => effect.id === id)
        );

        if (!hasExistingSelectionForObj) {
          setSelectedEffects([objEffects[0].id]);
        }
      }
    }
  }, [
    completeImageObject?.selectedObject,
    selectedObject,
    selectedEffects,
    setSelectedObject,
    setExpandedItems,
    setSelectedEffects,
  ]);

  // Sync panels based on selection: Expand Effects if none selected, Collapse if selected
  useEffect(() => {
    if (state.selectedEffects.length > 0) {
      state.setAnimationEffectsExpanded(false);
      state.setAnimationSettingsExpanded(true);
    } else {
      state.setAnimationEffectsExpanded(true);
    }
  }, [state.selectedEffects]);

  // Sync Animation Component local selections back to Global (catch-all for drops, etc.)
  useEffect(() => {
    if (!state.selectedObject || window_type === 'preview') return;
    if (lastGlobalObjId.current === state.selectedObject) return;

    lastGlobalObjId.current = state.selectedObject;

    if (typeof window.__selectObjectInScene === 'function') {
      window.__selectObjectInScene(state.selectedObject);
    }
  }, [state.selectedObject, window_type]);

  const updateCurrentTimeIndicator = useCallback(
    (positionPercent, time, visible = true) => {
      state.setCurrentTimeIndicator({
        position: Math.max(0, Math.min(100, positionPercent)),
        time: Math.max(0, time),
        visible: visible,
      });
    },
    [state.setCurrentTimeIndicator]
  );

  // Your CustomTimelineSlider (100% unchanged)
  const CustomTimelineSlider = ({ value, onChange, min, max, step }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [displayValue, setDisplayValue] = useState(value);
    const sliderRef = useRef(null);
    const rectRef = useRef(null);
    const rafIdRef = useRef(null);
    const pendingValueRef = useRef(null);
    const debounceTimerRef = useRef(null);

    useEffect(() => {
      if (!isDragging) {
        setDisplayValue(value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const percentage = ((displayValue - min) / (max - min)) * 100;

    const handleMouseDown = (e) => {
      e.preventDefault();
      setIsDragging(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
      if (sliderRef.current) {
        rectRef.current = sliderRef.current.getBoundingClientRect();
      }
    };

    const handleMouseMove = useCallback(
      (e) => {
        if (!isDragging) return;

        const rect =
          rectRef.current ||
          (sliderRef.current && sliderRef.current.getBoundingClientRect());
        if (!rect) return;

        const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
        const newValue = min + (x / rect.width) * (max - min);
        const steppedValue = Math.round(newValue / step) * step;
        const clampedValue = Math.max(min, Math.min(max, steppedValue));

        pendingValueRef.current = clampedValue;
        if (rafIdRef.current == null) {
          rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null;
            if (pendingValueRef.current != null) {
              setDisplayValue(pendingValueRef.current);
            }
          });
        }
      },
      [isDragging, min, max, onChange, step]
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      const finalValue =
        pendingValueRef.current != null
          ? pendingValueRef.current
          : displayValue;
      const snapped = Math.round(finalValue / step) * step;
      const clamped = Math.max(min, Math.min(max, snapped));
      setDisplayValue(clamped);
      onChange({ target: { value: clamped } });
      pendingValueRef.current = null;
    }, [displayValue, step, min, max, onChange]);

    const handleClick = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const newValue = min + (x / rect.width) * (max - min);
      const steppedValue = Math.round(newValue / step) * step;
      const clamped = Math.max(min, Math.min(max, steppedValue));
      setDisplayValue(clamped);
      onChange({ target: { value: clamped } });
    };

    useEffect(() => {
      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return (
      <div
        ref={sliderRef}
        className={`relative w-32 h-[2px] bg-[#8E8E8E] rounded-[5px] border border-gray-600 custom-timeline-slider ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        <div
          className={`absolute h-[2px] bg-gray-400  ${isDragging ? '' : 'transition-all duration-150'}`}
          style={{ width: `${percentage}%` }}
        />
        <div
          className={`absolute w-3 h-3 bg-[#D9D9D9] rounded-full border-2  transform -translate-x-1/2 top-1/2 -translate-y-1/2 cursor-grab shadow-md ${isDragging ? 'cursor-grabbing' : 'transition-all duration-150 hover:scale-105'}`}
          style={{ left: `${percentage}%` }}
        />
      </div>
    );
  };

  const handleZoomSliderChange = (e) => {
    const newScale = roundToPrecision(parseFloat(e.target.value), 1);

    const baseDuration = 7;
    const newDuration = Math.max(1, baseDuration / newScale);
    const currentCenter =
      state.effectStartTime + (state.effectEndTime - state.effectStartTime) / 2;
    const newStartTime = Math.max(0, currentCenter - newDuration / 2);
    const newEndTime = newStartTime + newDuration;

    state.setEffectStartTime(newStartTime);
    state.setEffectEndTime(newEndTime);
    state.setTimelineScale(newScale);
  };

  const handleDeleteAnimationObject = (objectId) => {
    dispatch(removeAnimationObject(objectId));
    state.setExpandedItems((prev) => {
      const newExpanded = { ...prev };
      delete newExpanded[objectId];
      return newExpanded;
    });
    state.setTimelineEffects((prev) =>
      prev.filter((effect) => effect.objectId !== objectId)
    );
    if (state.selectedObject === objectId) {
      state.setSelectedObject(null);
      state.setSelectedEffects([]);
      state.setAnimationSettings({
        duration: 3.0,
        startTime: 0.0,
        easing: 'None',
        activeAxes: { x: true, y: false, z: true },
        startPosition: { x: 1, y: 0, z: 0 },
        endPosition: { x: 10, y: 0, z: 0 },
        loop: false,
      });
    }
  };

  // Drag and drop handlers
  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.name) dispatch(addAnimationObject(data));
      state.setExpandedItems((prev) => ({ ...prev, [data.id]: true }));
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleEffectDragStart = (e, effect) => {
    state.setDraggedEffect(effect);
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'animationEffect',
        effect: effect,
      })
    );
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleEffectDragEnd = () => {
    state.setDraggedEffect(null);
    state.setIsDraggingOverTimeline(false);
  };

  const handleTimelineDragLeave = () => {
    state.setIsDraggingOverTimeline(false);
  };

  const handleTimelineDrop = (e, objectId) => {
    e.preventDefault();
    state.setIsDraggingOverTimeline(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'animationEffect') {
        const DEFAULT_NEW_EFFECT_DURATION = 3;
        const indicatorTime =
          state.currentTimeIndicator.time || state.animationSettings.startTime;
        const startTime = Math.max(0, indicatorTime);
        const endTime = Math.min(
          calculateTimelineBounds.actualMaxEndTime || state.maxTimelineDuration,
          startTime + DEFAULT_NEW_EFFECT_DURATION
        );

        // Get current timeline effects for this object to calculate available row
        const currentObjectEffects = state.timelineEffects.filter(
          (effect) => effect.objectId === objectId
        );

        // Find available row without modifying existing effects
        let availableRow = 0;
        const hasTimeOverlap = (start1, end1, start2, end2) => {
          return !(end1 <= start2 || start1 >= end2);
        };

        // Find the first row where there's no overlap
        for (let row = 0; row < 20; row++) {
          const effectsInRow = currentObjectEffects.filter(
            (effect) => (effect.row || 0) === row
          );
          const hasOverlap = effectsInRow.some((effect) => {
            return hasTimeOverlap(
              startTime,
              endTime,
              effect.startTime,
              effect.endTime
            );
          });

          if (!hasOverlap) {
            availableRow = row;
            break;
          }

          // If we've checked all rows with effects, use the next available row
          if (
            row >= Math.max(...currentObjectEffects.map((e) => e.row || 0), -1)
          ) {
            availableRow = row + 1;
            break;
          }
        }

        const newEffect = {
          id: `${data.effect.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // More unique ID
          effectType: data.effect.id,
          name: data.effect.name,
          icon: data.effect.icon,
          objectId: objectId,
          startTime: startTime,
          endTime: endTime,
          duration: DEFAULT_NEW_EFFECT_DURATION,
          row: availableRow,
          easing: state.animationSettings.easing,
          activeAxes: { ...state.animationSettings.activeAxes },
          startPosition: { ...state.animationSettings.startPosition },
          endPosition: { ...state.animationSettings.endPosition },
          loop: state.animationSettings.loop,
        };

        state.setTimelineEffects((prev) => [...prev, newEffect]);
        onObjectSelect?.({ id: objectId });
        state.setSelectedObject(objectId);
        state.setSelectedEffects([newEffect.id]);

        if (endTime > timelineBounds.endTime) {
          state.setEffectEndTime(endTime + 2);
          state.setMaxTimelineDuration(
            Math.max(state.maxTimelineDuration, endTime + 2)
          );
        }
      }
    } catch (error) {
      console.error('Error handling timeline drop:', error);
    }
  };

  const handleTimeMarkerClick = (e) => {
    // Pause animation if playing when user clicks timeline
    if (state.isPlaying) {
      state.setIsPlaying(false);
      state.setPlaybackStartTime(null);
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const { timePosition, positionPercent } = calculateTimelinePosition(
      e.clientX,
      rect
    );
    updateCurrentTimeIndicator(positionPercent, timePosition, true);
  };

  // Updated findNextAvailableRow function - More robust
  const findNextAvailableRow = (
    objectId,
    startTime,
    endTime,
    excludeEffectId = null
  ) => {
    const objectEffects = state.timelineEffects.filter(
      (effect) => effect.objectId === objectId && effect.id !== excludeEffectId
    );

    // Function to check if two time ranges overlap
    const hasTimeOverlap = (start1, end1, start2, end2) => {
      return !(end1 <= start2 || start1 >= end2);
    };

    for (let row = 0; row < 20; row++) {
      const effectsInRow = objectEffects.filter(
        (effect) => (effect.row || 0) === row
      );
      const hasOverlap = effectsInRow.some((effect) => {
        return hasTimeOverlap(
          startTime,
          endTime,
          effect.startTime,
          effect.endTime
        );
      });

      if (!hasOverlap) {
        return row;
      }
    }

    return objectEffects.length;
  };

  const handleTimelineEffectDragStart = (e, effect) => {
    e.stopPropagation();
    state.setDraggedTimelineEffect(effect);
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    state.setDragOffset({ x: offsetX, y: 0 });

    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'timelineEffect',
        effectId: effect.id,
        effect: effect,
      })
    );
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTimelineEffectDragEnd = (e) => {
    e.stopPropagation();
    state.setDraggedTimelineEffect(null);
    state.setDragOffset({ x: 0, y: 0 });
    state.setIsDraggingOverTimeline(false);
  };

  const handleTimelineEffectDrop = (e, targetObjectId) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      if (data.type === 'timelineEffect' && state.draggedTimelineEffect) {
        const rect = e.currentTarget.getBoundingClientRect();
        const { timePosition } = calculateTimelinePosition(
          e.clientX - state.dragOffset.x,
          rect
        );

        const effectDuration =
          state.draggedTimelineEffect.endTime -
          state.draggedTimelineEffect.startTime;
        const newStartTime = Math.max(0, timePosition);
        const newEndTime = newStartTime + effectDuration;

        // Get current effects for target object (excluding the dragged effect)
        const targetObjectEffects = state.timelineEffects.filter(
          (effect) =>
            effect.objectId === targetObjectId &&
            effect.id !== state.draggedTimelineEffect.id
        );

        // Find available row
        let availableRow = 0;
        const hasTimeOverlap = (start1, end1, start2, end2) => {
          return !(end1 <= start2 || start1 >= end2);
        };

        for (let row = 0; row < 20; row++) {
          const effectsInRow = targetObjectEffects.filter(
            (effect) => (effect.row || 0) === row
          );
          const hasOverlap = effectsInRow.some((effect) => {
            return hasTimeOverlap(
              newStartTime,
              newEndTime,
              effect.startTime,
              effect.endTime
            );
          });

          if (!hasOverlap) {
            availableRow = row;
            break;
          }

          if (
            row >= Math.max(...targetObjectEffects.map((e) => e.row || 0), -1)
          ) {
            availableRow = row + 1;
            break;
          }
        }

        state.setTimelineEffects((prev) =>
          prev.map((effect) =>
            effect.id === state.draggedTimelineEffect.id
              ? {
                  ...effect,
                  startTime: newStartTime,
                  endTime: newEndTime,
                  objectId: targetObjectId,
                  row: availableRow,
                }
              : effect
          )
        );
        onObjectSelect?.({ id: targetObjectId });
        state.setSelectedObject(targetObjectId);
        state.setSelectedEffects([state.draggedTimelineEffect.id]);

        if (newEndTime > timelineBounds.endTime) {
          state.setEffectEndTime(newEndTime + 2);
          state.setMaxTimelineDuration(
            Math.max(state.maxTimelineDuration, newEndTime + 2)
          );
        }
      }
    } catch (error) {
      console.error('Error handling timeline effect drop:', error);
    }
  };

  const roundToPrecision = (num, precision = 1) => {
    return Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision);
  };

  const parseFloatWithPrecision = (value, precision = 1) => {
    const parsed = parseFloat(value) || 0;
    return roundToPrecision(parsed, precision);
  };

  // Mouse handler for the time indicator dragging
  const handleIndicatorMouseDown = useCallback(
    (e, onScrub, safeTime, setDraggingState) => {
      e.preventDefault();
      e.stopPropagation();

      // Pause animation if playing when user starts dragging indicator
      if (state.isPlaying) {
        state.setIsPlaying(false);
        if (state.setPlaybackStartTime) state.setPlaybackStartTime(null);
      }

      const setter = setDraggingState || state.setIsDraggingIndicator;
      setter(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      // Immediate scrubbing update on click
      if (onScrub) onScrub(safeTime);
    },
    [
      state.isPlaying,
      state.setIsPlaying,
      state.setPlaybackStartTime,
      state.setIsDraggingIndicator,
    ]
  );

  // FIXED: Load settings from selected effect and sync timeline indicator
  const loadSettingsFromSelectedEffect = useCallback(() => {
    if (state.selectedEffects.length !== 1) return;

    const selectedEffect = state.timelineEffects.find(
      (effect) => effect.id === state.selectedEffects[0]
    );
    if (!selectedEffect) return;

    const nextSettings = {
      duration:
        selectedEffect.duration ||
        selectedEffect.endTime - selectedEffect.startTime,
      startTime: selectedEffect.startTime,
      easing: selectedEffect.easing || 'None',
      activeAxes: selectedEffect.activeAxes || {
        x: true,
        y: false,
        z: true,
      },
      startPosition: selectedEffect.startPosition || { x: 1, y: 0, z: 0 },
      endPosition: selectedEffect.endPosition || { x: 10, y: 0, z: 0 },
      loop: selectedEffect.loop || false,
    };

    state.setAnimationSettings((prev) => {
      const unchanged =
        prev.duration === nextSettings.duration &&
        prev.startTime === nextSettings.startTime &&
        prev.easing === nextSettings.easing &&
        prev.loop === nextSettings.loop &&
        JSON.stringify(prev.activeAxes) ===
          JSON.stringify(nextSettings.activeAxes) &&
        JSON.stringify(prev.startPosition) ===
          JSON.stringify(nextSettings.startPosition) &&
        JSON.stringify(prev.endPosition) ===
          JSON.stringify(nextSettings.endPosition);

      return unchanged ? prev : { ...prev, ...nextSettings };
    });
  }, [
    state.selectedEffects,
    state.timelineEffects,
    state.setAnimationSettings,
  ]);

  useEffect(() => {
    loadSettingsFromSelectedEffect();
  }, [state.selectedEffects, state.timelineEffects]);

  // Redundant mouse handlers removed - handled by AnimationTimelinePanel.jsx

  // Updated: Apply settings to selected effects without moving time indicator
  const applySettingsToSelectedEffects = (settingKey, value) => {
    if (state.selectedEffects.length > 0) {
      state.setTimelineEffects((prev) =>
        prev.map((effect) => {
          if (state.selectedEffects.includes(effect.id)) {
            if (settingKey === 'duration') {
              const newEndTime = effect.startTime + value;

              // Check for overlaps when duration changes and handle conflicts
              const conflictingEffects = prev.filter(
                (e) =>
                  e.objectId === effect.objectId &&
                  e.id !== effect.id &&
                  (e.row || 0) === (effect.row || 0) &&
                  !(newEndTime <= e.startTime || effect.startTime >= e.endTime)
              );

              // Move conflicting effects to next available rows
              if (conflictingEffects.length > 0) {
                conflictingEffects.forEach((conflictingEffect) => {
                  const nextRow = findNextAvailableRow(
                    effect.objectId,
                    conflictingEffect.startTime,
                    conflictingEffect.endTime,
                    conflictingEffect.id
                  );
                  state.setTimelineEffects((current) =>
                    current.map((e) =>
                      e.id === conflictingEffect.id ? { ...e, row: nextRow } : e
                    )
                  );
                });
              }

              return {
                ...effect,
                duration: value,
                endTime: newEndTime,
              };
            } else if (settingKey === 'startTime') {
              const duration = effect.endTime - effect.startTime;
              const newEndTime = value + duration;

              // Check for overlaps when start time changes and handle conflicts
              const conflictingEffects = prev.filter(
                (e) =>
                  e.objectId === effect.objectId &&
                  e.id !== effect.id &&
                  (e.row || 0) === (effect.row || 0) &&
                  !(newEndTime <= e.startTime || value >= e.endTime)
              );

              // Move conflicting effects to next available rows
              if (conflictingEffects.length > 0) {
                conflictingEffects.forEach((conflictingEffect) => {
                  const nextRow = findNextAvailableRow(
                    effect.objectId,
                    conflictingEffect.startTime,
                    conflictingEffect.endTime,
                    conflictingEffect.id
                  );
                  state.setTimelineEffects((current) =>
                    current.map((e) =>
                      e.id === conflictingEffect.id ? { ...e, row: nextRow } : e
                    )
                  );
                });
              }

              return {
                ...effect,
                startTime: value,
                endTime: newEndTime,
              };
            } else {
              return {
                ...effect,
                [settingKey]: value,
              };
            }
          }
          return effect;
        })
      );
    }
  };

  const toggleAnimationEffects = () => {
    state.setAnimationEffectsExpanded((prev) => !prev);
  };

  const toggleAnimationSettings = () => {
    state.setAnimationSettingsExpanded((prev) => !prev);
  };

  const toggleAxis = (axis) => {
    state.setAnimationSettings((prev) => ({
      ...prev,
      activeAxes: {
        ...prev.activeAxes,
        [axis]: !prev.activeAxes[axis],
      },
    }));
    // Apply to selected effects
    applySettingsToSelectedEffects('activeAxes', {
      ...state.animationSettings.activeAxes,
      [axis]: !state.animationSettings.activeAxes[axis],
    });
  };

  const updateSetting = (key, value) => {
    const validValue =
      typeof value === 'number'
        ? Math.max(0, roundToPrecision(value, 1))
        : value;
    state.setAnimationSettings((prev) => ({
      ...prev,
      [key]: validValue,
    }));

    // Don't update time indicator for start time changes - only update effect positions
    applySettingsToSelectedEffects(key, validValue);
  };

  const updatePosition = (positionType, axis, value) => {
    const numValue = parseFloat(value) || 0;
    const newPosition = {
      ...state.animationSettings[positionType],
      [axis]: numValue,
    };

    state.setAnimationSettings((prev) => ({
      ...prev,
      [positionType]: newPosition,
    }));

    // Apply to selected effects
    applySettingsToSelectedEffects(positionType, newPosition);
  };

  const handleObjectSelect = (obj) => {
    state.setSelectedObject(obj.id);

    // Sync Animation Component -> global selection (skip in preview / before controls exist)
    if (
      window_type !== 'preview' &&
      typeof window.__selectObjectInScene === 'function'
    ) {
      window.__selectObjectInScene(obj.id);
    }

    const objEffects = state.timelineEffects.filter(
      (effect) => effect.objectId === obj.id
    );

    const hasExistingSelectionForObj = state.selectedEffects.some((id) =>
      objEffects.some((effect) => effect.id === id)
    );

    if (!hasExistingSelectionForObj) {
      if (objEffects.length > 0) {
        state.setSelectedEffects([objEffects[0].id]);
      } else {
        state.setSelectedEffects([]);
      }
    }
  };

  // Effect selection handler
  const handleEffectSelect = (effect, obj, e) => {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      state.setSelectedEffects((prev) =>
        prev.includes(effect.id)
          ? prev.filter((id) => id !== effect.id)
          : [...prev, effect.id]
      );
    } else {
      state.setSelectedEffects([effect.id]);
      state.setSelectedObject(obj.id);
    }
  };

  // Clear selection handler
  const clearSelection = (e) => {
    if (e && e.target.closest('.timeline-effect')) {
      return;
    }
    state.setSelectedEffects([]);
    state.setSelectedObject(null);
  };

  const renderTimeIndicator = (
    position,
    time,
    isVisible,
    setIsDraggingIndicator,
    isDraggingIndicator,
    onScrub,
    indicatorRef,
    timeTextRef
  ) => {
    if (!isVisible) return null;

    const safePosition = Math.max(0, Math.min(100, position || 0));
    const safeTime = Math.max(0, time || 0);

    // Use passed parameters or fall back to state
    const draggingState =
      isDraggingIndicator !== undefined
        ? isDraggingIndicator
        : state.isDraggingIndicator;
    const setDraggingState =
      setIsDraggingIndicator || state.setIsDraggingIndicator;

    return (
      <div
        ref={indicatorRef}
        className="absolute top-0 bottom-0 pointer-events-none z-30"
        style={{ left: `${safePosition}%` }}
      >
        {/* Invisible larger hit area for easier dragging */}
        <div
          className="absolute top-[16px] left-1/2 w-10 h-8 transform -translate-x-1/2 pointer-events-auto cursor-grab"
          onMouseDown={(e) =>
            handleIndicatorMouseDown(e, onScrub, safeTime, setDraggingState)
          }
        />
        <div
          className={`absolute top-[28px] left-1/2 w-8 h-1.5 bg-[#EE2B2A] rounded-full transform -translate-x-1/2 pointer-events-auto cursor-grab shadow-sm ${
            draggingState ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          title="Drag to change timeline position"
          onMouseDown={(e) =>
            handleIndicatorMouseDown(e, onScrub, safeTime, setDraggingState)
          }
        />
        <div
          className="absolute top-[33px] left-1/2 w-0 h-0 
        border-l-[7px] border-r-[7px] border-t-[10px] 
        border-l-transparent border-r-transparent border-[#EE2B2A] 
        transform -translate-x-1/2 pointer-events-auto cursor-grab"
          onMouseDown={(e) =>
            handleIndicatorMouseDown(e, onScrub, safeTime, setDraggingState)
          }
        />
        <div className="absolute top-[43px] left-1/2 w-0.5 bottom-4 bg-[#EE2B2A] transform -translate-x-1/2" />

        <div
          ref={timeTextRef}
          className="absolute top-[108px] left-1/2 text-[10px] text-[#EE2B2A] font-mono bg-black px-1 rounded transform -translate-x-1/2 whitespace-nowrap"
        >
          {safeTime.toFixed(2)}s
        </div>
      </div>
    );
  };

  return (
    <div
      id="timeline-panel"
      className={`bg-[#131418] fixed left-0 bottom-0 z-[999]  text-white w-screen border-t border-b border-[#4D4D4D] select-none ${state.isDragging ? '' : 'transition-all duration-300 ease-in-out'} ${
        state.isMinimized ? 'h-10 ' : ''
      } flex`}
      style={{
        height: state.isMinimized ? '40px' : `${state.animationHeight}px`,
      }}
    >
      {/* Resize handle - Always visible for both collapsed and expanded states */}
      <div
        className="absolute top-0 left-0 w-full h-1 cursor-ns-resize hover:bg-red-500/50 transition-colors z-50"
        onMouseDown={handleMouseDown}
        style={{
          background: state.isDragging
            ? 'rgba(239, 68, 68, 0.5)'
            : 'transparent',
        }}
      />

      {/* Left Panel */}
      <AnimationLeftPanel
        isMinimized={state.isMinimized}
        setIsMinimized={state.setIsMinimized}
        currentTimeIndicator={state.currentTimeIndicator}
        onDrop={(data) => {
          state.setExpandedItems((prev) => ({ ...prev, [data.id]: true }));
        }}
        onDragOver={(e) => e.preventDefault()}
        selectedObject={state.selectedObject}
        onObjectSelect={handleObjectSelect}
        onDeleteAnimationObject={handleDeleteAnimationObject}
        timelineEffects={state.timelineEffects}
      />

      {/* Middle Panel - Timeline */}
      <AnimationTimelinePanel
        completeImageObject={completeImageObject}
        timelineEffects={state.timelineEffects}
        setTimelineEffects={state.setTimelineEffects}
        selectedObject={state.selectedObject}
        setSelectedObject={state.setSelectedObject}
        selectedEffects={state.selectedEffects}
        setSelectedEffects={state.setSelectedEffects}
        isDraggingOverTimeline={state.isDraggingOverTimeline}
        setIsDraggingOverTimeline={state.setIsDraggingOverTimeline}
        draggedTimelineEffect={state.draggedTimelineEffect}
        setDraggedTimelineEffect={state.setDraggedTimelineEffect}
        dragOffset={state.dragOffset}
        setDragOffset={state.setDragOffset}
        window_type={window_type}
        draggedEffect={state.draggedEffect}
        setDraggedEffect={state.setDraggedEffect}
        timelineScale={state.timelineScale}
        setTimelineScale={state.setTimelineScale}
        effectStartTime={state.effectStartTime}
        setEffectStartTime={state.setEffectStartTime}
        effectEndTime={state.effectEndTime}
        setEffectEndTime={state.setEffectEndTime}
        maxTimelineDuration={state.maxTimelineDuration}
        setMaxTimelineDuration={state.setMaxTimelineDuration}
        currentTimeIndicator={state.currentTimeIndicator}
        updateCurrentTimeIndicator={updateCurrentTimeIndicator}
        isDraggingIndicator={state.isDraggingIndicator}
        setIsDraggingIndicator={state.setIsDraggingIndicator}
        animationSettings={state.animationSettings}
        isPlaying={state.isPlaying}
        setIsPlaying={state.setIsPlaying}
        setPlaybackStartTime={state.setPlaybackStartTime}
        calculateTimelineBounds={timelineBounds}
        findNextAvailableRow={findNextAvailableRow}
        timeToPercent={timeToPercent}
        calculateTimelinePosition={calculateTimelinePosition}
        roundToPrecision={roundToPrecision}
        parseFloatWithPrecision={parseFloatWithPrecision}
        handleTimelineDrop={timelineHandlers.handleTimelineDrop}
        handleTimelineEffectDrop={timelineHandlers.handleTimelineEffectDrop}
        handleTimelineEffectDragStart={
          timelineHandlers.handleTimelineEffectDragStart
        }
        handleTimelineEffectDragEnd={
          timelineHandlers.handleTimelineEffectDragEnd
        }
        handleTimelineDragLeave={timelineHandlers.handleTimelineDragLeave}
        handleTimeMarkerClick={timelineHandlers.handleTimeMarkerClick}
        handleEffectResize={timelineHandlers.handleEffectResize}
        removeTimelineEffect={timelineHandlers.removeTimelineEffect}
        handleEffectSelect={handleEffectSelect}
        clearSelection={clearSelection}
        handlePlayPause={playbackHandlers.handlePlayPause}
        handleGoToStart={playbackHandlers.handleGoToStart}
        handleGoToEnd={playbackHandlers.handleGoToEnd}
        handleZoomSliderChange={handleZoomSliderChange}
        updateSetting={animationSettingsHandlers.updateSetting}
        CustomTimelineSlider={CustomTimelineSlider}
        renderTimeIndicator={renderTimeIndicator}
      />

      {/* Right Panel */}
      <AnimationRightPanel
        animationEffectsExpanded={state.animationEffectsExpanded}
        setAnimationEffectsExpanded={state.setAnimationEffectsExpanded}
        animationSettingsExpanded={state.animationSettingsExpanded}
        setAnimationSettingsExpanded={state.setAnimationSettingsExpanded}
        animationEffects={ANIMATION_EFFECTS}
        draggedEffect={state.draggedEffect}
        setDraggedEffect={state.setDraggedEffect}
        animationSettings={state.animationSettings}
        selectedEffects={state.selectedEffects}
        timelineEffects={state.timelineEffects}
        completeImageObject={completeImageObject}
        handleEffectDragStart={timelineHandlers.handleEffectDragStart}
        handleEffectDragEnd={timelineHandlers.handleEffectDragEnd}
        toggleAnimationEffects={() =>
          state.setAnimationEffectsExpanded((prev) => !prev)
        }
        toggleAnimationSettings={() =>
          state.setAnimationSettingsExpanded((prev) => !prev)
        }
        toggleAxis={animationSettingsHandlers.toggleAxis}
        updateSetting={animationSettingsHandlers.updateSetting}
        updateSelectedEffectsProperty={
          animationSettingsHandlers.updateSelectedEffectsProperty
        }
        updatePosition={animationSettingsHandlers.updatePosition}
        roundToPrecision={roundToPrecision}
        parseFloatWithPrecision={parseFloatWithPrecision}
        isMinimized={state.isMinimized}
        setIsMinimized={state.setIsMinimized}
      />
    </div>
  );
};

export default AnimationComponentNew;

AnimationComponentNew.propTypes = {
  onMinimizeChange: PropTypes.func,
  completeImageObject: PropTypes.any,
  initialMinimized: PropTypes.bool,
  forceShow: PropTypes.bool,
  forceShowSignal: PropTypes.number,
};
